import { useState, useEffect } from "react";

import {
  Button,
  Datepicker,
  HelperText,
  Label,
  Modal,
  Radio,
  Select,
  TextInput,
} from "flowbite-react";
import { useTranslation } from "react-i18next";
import { HiPlus } from "react-icons/hi";
import { useAuth, useToast } from "../../../../hooks";
import { NOTIFICATION_TYPES } from "../../../../utils";

const OPP_TYPE_MAPPING = {
  featureRequested: "FEATURE_REQUEST",
  extraSeats: "EXTRA_SEATS",
  upgradeSubscription: "UPGRADE_SUBSCRIPTION",
};

export const AddEditExpansionOpportunity = ({
  customerData,
  selectedOpportunity,
  setSelectedOpportunity,
  fetchExpansionOpportunities,
}) => {
  const { apiClient } = useAuth();
  const [openModal, setOpenModal] = useState(false);
  const [activationDate, setActivationDate] = useState(new Date());
  const showToast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedOpportunity) {
      setOpenModal(true);
      setActivationDate(
        selectedOpportunity.activation_date
          ? new Date(selectedOpportunity.activation_date)
          : new Date(),
      );
    }
  }, [selectedOpportunity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean currency symbols from additionalMrr
    const cleanAdditionalMrr = data.additionalMrr
      ? parseFloat(data.additionalMrr.replace(/[€$£¥,\s]/g, ""))
      : 0;

    const dataToSend = {
      name: data.name,
      additional_mrr: cleanAdditionalMrr,
      activation_date: activationDate.toISOString(),
      type: data.opType,
      contract: data.contract,
    };

    try {
      const response = selectedOpportunity
        ? await apiClient.put(
            `/contracts/${dataToSend.contract}/expansion-opportunities/${selectedOpportunity.id}/`,
            dataToSend,
          )
        : await apiClient.post(
            `/contracts/${dataToSend.contract}/expansion-opportunities/`,
            dataToSend,
          );

      if (response.status === 201) {
        showToast({
          content: "Expansion opportunity saved successfully",
          type: NOTIFICATION_TYPES.success,
        });
      } else if (response.status === 200) {
        showToast({
          content: "Expansion opportunity updated successfully",
          type: NOTIFICATION_TYPES.success,
        });
      }
      fetchExpansionOpportunities(customerData.id);
    } catch (err) {
      const errors = err.response.data;
      const toastText = Object.entries(errors)
        .map(([key, messages]) => {
          // Capitalize the first letter of the key
          const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
          // Join multiple error messages with a comma and space
          const combinedMessages = messages.join(", ");
          return `${formattedKey}: ${combinedMessages}`;
        })
        .join("\n");
      console.error("Error saving expansion opportunity:", err);

      showToast({
        content: toastText,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setOpenModal(false);
    }
  };

  return (
    <>
      <Button color="light" size="sm" onClick={() => setOpenModal(true)}>
        Add expansion
        <HiPlus className="ml-2 h-5 w-5" />
      </Button>
      <Modal
        show={openModal}
        popup
        onClose={() => {
          setOpenModal(false);
          setSelectedOpportunity(null);
        }}
      >
        <Modal.Header className="pl-5 pr-2 pt-3">
          <h3 className="text-xl font-medium text-gray-900 dark:text-white">
            Add expansion opportunity
          </h3>
          <div className="text-xs font-normal leading-none text-gray-500 opacity-80">
            Add a new expansion features & account opportunity
          </div>
        </Modal.Header>
        <Modal.Body className="p-0 pb-5">
          <form onSubmit={handleSubmit} className="mt-3 space-y-5 px-5">
            <TextInput
              id="name"
              name="name"
              placeholder="Give the expansion opp a name"
              defaultValue={selectedOpportunity?.name}
              required
            />
            <TextInput
              id="additionalMrr"
              name="additionalMrr"
              type="number"
              placeholder="Potential additional €MRR this opportunity brings"
              defaultValue={selectedOpportunity?.additional_mrr.amount}
              required
            />
            <Datepicker
              id="date"
              placeholder="Expected close date"
              value={activationDate}
              onSelectedDateChanged={setActivationDate}
              minDate={new Date()}
            />
            <div>
              <div className="mb-2 block">
                <Label htmlFor="contract">Select your contract</Label>
              </div>
              <Select
                id="contract"
                name="contract"
                defaultValue={selectedOpportunity?.contract}
                color={customerData.contracts.length === 0 ? "failure" : "gray"}
                required
              >
                {customerData.contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.name}
                  </option>
                ))}
              </Select>
              {customerData.contracts.length === 0 && (
                <HelperText>
                  There are no contracts available for this customer. Please
                  create a contract first.
                </HelperText>
              )}
            </div>

            <fieldset className="flex max-w-md flex-col gap-1">
              <legend className="mb-4 text-xs font-normal leading-none text-gray-500">
                Opportunity type
              </legend>
              {Object.entries(OPP_TYPE_MAPPING).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Radio
                    id={key}
                    name="opType"
                    value={value}
                    defaultChecked={selectedOpportunity?.type === value}
                  />
                  <Label htmlFor={key}>
                    {t(`common.customer.expansionOppTypes.${value}`)}
                  </Label>
                </div>
              ))}
            </fieldset>
            <div className="flex w-full items-end justify-end">
              <Button color="blue" type="submit">
                Add
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};
